import { Box, Checkbox, Link, VStack } from '@chakra-ui/react';
import { useState } from 'react';
import { FaStethoscope } from 'react-icons/fa6';
import { Text } from '../../../../components/atoms/Text';
import { Button } from '../../../../components/atoms/Button';
import { useTheme } from '../../../../contexts/ThemeProvider';

interface ConsentStepProps {
  onAccepted: () => void;
}

export function ConsentStep({ onAccepted }: ConsentStepProps) {
  const { name, colors } = useTheme();
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [acceptedInformedConsent, setAcceptedInformedConsent] = useState(false);
  const [acceptedDataProcessing, setAcceptedDataProcessing] = useState(false);

  const canContinue = acceptedTerms && acceptedInformedConsent && acceptedDataProcessing;

  return (
    <VStack spacing={4} align={'stretch'} width={'100%'}>
      <VStack spacing={1} align={'start'}>
        <Box
          width={'40px'}
          height={'40px'}
          borderRadius={'full'}
          display={'flex'}
          alignItems={'center'}
          justifyContent={'center'}
          backgroundColor={'#F7F9FA'}
        >
          <FaStethoscope size={'18px'} color={colors.primaryColor} />
        </Box>
        <Text fontSize={'16px'} fontWeight={700}>
          Termos, consentimento informado e proteção de dados
        </Text>
        <Text fontSize={'13px'} color={'#4A5568'}>
          Bem-vindo ao serviço de Telemedicina da {name}. Antes de continuar, leia e concorde com os pontos abaixo —
          eles seguem a Resolução CFM nº 2.314/2022 (telemedicina) e a Lei Geral de Proteção de Dados (LGPD).
        </Text>
      </VStack>

      <Box width={'100%'} padding={'10px'} borderRadius={'12px'} backgroundColor={'#FFF5F5'} border={'1px solid #FEB2B2'}>
        <Text fontSize={'13px'} color={'#742A2A'}>
          <Text as="span" fontWeight={700}>
            Alerta de segurança:
          </Text>{' '}
          Este canal oferece atendimento médico online e{' '}
          <Text as="span" fontWeight={700}>
            não deve ser utilizado para emergências médicas graves
          </Text>{' '}
          (como infarto ou AVC). Nesses casos, ligue para o SAMU (192) ou dirija-se ao pronto-socorro mais próximo.
        </Text>
      </Box>

      <VStack spacing={2} width={'100%'} align={'stretch'}>
        <Box
          as="label"
          width={'100%'}
          padding={'10px'}
          borderRadius={'12px'}
          border={'1px solid'}
          borderColor={acceptedTerms ? colors.primaryColor : '#E2E2E2'}
          backgroundColor={acceptedTerms ? '#F7F9FA' : 'transparent'}
          cursor={'pointer'}
        >
          <Checkbox isChecked={acceptedTerms} onChange={(e) => setAcceptedTerms(!!e.target.checked)} alignItems={'flex-start'}>
            <Text fontSize={'14px'} fontWeight={400}>
              Eu li e concordo com os{' '}
              <Link
                href={'/legal/termos-de-uso'}
                isExternal
                color={colors.primaryColor}
                textDecoration={'underline'}
                onClick={(e) => e.stopPropagation()}
              >
                termos de uso
              </Link>{' '}
              da plataforma.
            </Text>
          </Checkbox>
        </Box>

        <Box
          as="label"
          width={'100%'}
          padding={'10px'}
          borderRadius={'12px'}
          border={'1px solid'}
          borderColor={acceptedInformedConsent ? colors.primaryColor : '#E2E2E2'}
          backgroundColor={acceptedInformedConsent ? '#F7F9FA' : 'transparent'}
          cursor={'pointer'}
        >
          <Checkbox
            isChecked={acceptedInformedConsent}
            onChange={(e) => setAcceptedInformedConsent(!!e.target.checked)}
            alignItems={'flex-start'}
          >
            <Text fontSize={'14px'} fontWeight={400}>
              Dou meu{' '}
              <Text as="span" fontWeight={700}>
                consentimento livre e esclarecido
              </Text>{' '}
              para atendimento por telemedicina (Resolução CFM nº 2.314/2022), ciente de que serei atendido por
              profissional devidamente registrado no CRM e de que o atendimento remoto tem limitações em relação ao
              presencial, podendo o médico indicar consulta presencial quando necessário.
            </Text>
          </Checkbox>
        </Box>

        <Box
          as="label"
          width={'100%'}
          padding={'10px'}
          borderRadius={'12px'}
          border={'1px solid'}
          borderColor={acceptedDataProcessing ? colors.primaryColor : '#E2E2E2'}
          backgroundColor={acceptedDataProcessing ? '#F7F9FA' : 'transparent'}
          cursor={'pointer'}
        >
          <Checkbox
            isChecked={acceptedDataProcessing}
            onChange={(e) => setAcceptedDataProcessing(!!e.target.checked)}
            alignItems={'flex-start'}
          >
            <Text fontSize={'14px'} fontWeight={400}>
              Autorizo, nos termos da LGPD (Lei nº 13.709/2018), o tratamento dos meus dados pessoais sensíveis —
              incluindo dados de saúde, documento de identificação, imagem e voz — exclusivamente para fins desta
              consulta, conforme a{' '}
              <Link
                href={'/legal/politica-de-privacidade'}
                isExternal
                color={colors.primaryColor}
                textDecoration={'underline'}
                onClick={(e) => e.stopPropagation()}
              >
                Política de Privacidade
              </Link>
              .
            </Text>
          </Checkbox>
        </Box>
      </VStack>

      <Button onClick={onAccepted} isDisabled={!canContinue}>
        Continuar
      </Button>
    </VStack>
  );
}
